/* Super-admin passkey enrolment page — /super-admin/enrol/:token
   ─────────────────────────────────────────────────────────────────────
   3-step wizard (iter98.99fm):
     1. Look up the invite → confirm identity to the invitee
     2. Verify phone OTP (Twilio Verify or DEV "123456")
     3. Register a passkey via WebAuthn navigator.credentials.create
        → server returns 10 recovery codes ONE TIME
        → we drop the invitee straight into /super-admin
*/
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    ShieldCheck, Phone, Fingerprint, CheckCircle, Warning,
    ArrowRight, Copy, Sparkle,
} from "@phosphor-icons/react";

export default function SuperAdminEnrol() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [step, setStep] = useState("loading"); // loading | intro | otp | passkey | done
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSending, setOtpSending] = useState(false);
    const [otpDevCode, setOtpDevCode] = useState("");   // shown in dev only
    const [verifying, setVerifying] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [passkeyLabel, setPasskeyLabel] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/super-admin/invites/${token}`);
                setInvite(r.data);
                if (r.data.status === "phone_verified") {
                    setStep("passkey");
                } else if (r.data.status === "pending") {
                    setStep("intro");
                } else {
                    setError("This invite has already been used or is no longer valid.");
                    setStep("done");
                }
            } catch (err) {
                setError(err.response?.data?.detail || "Invite not found or expired");
                setStep("done");
            }
        })();
    }, [token]);

    const sendOtp = async () => {
        setOtpSending(true);
        try {
            const r = await api.post(`/super-admin/invites/${token}/send-otp`);
            if (r.data.dev_mode) setOtpDevCode(r.data.dev_code || "");
            toast.success("OTP sent to your phone");
            setStep("otp");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send OTP");
        } finally {
            setOtpSending(false);
        }
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) {
            toast.error("Enter the 6-digit OTP");
            return;
        }
        setVerifying(true);
        try {
            await api.post(`/super-admin/invites/${token}/verify-phone`, { otp });
            toast.success("Phone verified");
            setStep("passkey");
        } catch (err) {
            toast.error(err.response?.data?.detail || "OTP verification failed");
        } finally {
            setVerifying(false);
        }
    };

    const registerPasskey = async () => {
        setRegistering(true);
        try {
            const opts = await api.post(`/super-admin/invites/${token}/registration-options`);
            const attResp = await startRegistration({ optionsJSON: opts.data.options });
            const verify = await api.post(`/super-admin/invites/${token}/verify-registration`, {
                credential: attResp,
                label: passkeyLabel || navigatorHint(),
            });
            // Persist the JWT and drop into super-admin dashboard.
            localStorage.setItem("ca_token", verify.data.token);
            setRecoveryCodes(verify.data.recovery_codes || []);
            setStep("done");
            toast.success("Passkey registered!");
        } catch (err) {
            const msg = err?.name === "NotAllowedError"
                ? "Passkey registration was cancelled — try again"
                : (err.response?.data?.detail || err.message || "Passkey registration failed");
            toast.error(msg);
        } finally {
            setRegistering(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-lg">
                <div className="flex items-center gap-2 mb-6">
                    <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/30 grid place-items-center text-primary">
                        <ShieldCheck size={20} weight="fill" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">Invisique™ · Super Admin</div>
                        <div className="text-sm font-semibold">Passkey enrolment</div>
                    </div>
                </div>

                {step === "loading" && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 text-center text-slate-400 text-sm">Loading invite…</div>
                )}

                {step === "intro" && invite && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8" data-testid="sa-enrol-intro">
                        <h1 className="text-2xl font-display font-semibold mb-1">Welcome, {invite.name || "Super Admin"}</h1>
                        <p className="text-sm text-slate-400 mb-6">
                            {invite.issued_by_name ? `${invite.issued_by_name} invited you` : "You have been invited"} to join Invisique™ as a Super Admin.
                            This is a phishing-resistant Passkey login — no passwords, no SMS-only fallback.
                        </p>
                        <ol className="space-y-3 text-sm mb-6">
                            <StepRow n={1} title="Verify your phone" body="We'll send a 6-digit code to the number on file." />
                            <StepRow n={2} title="Register a passkey" body="Your device will prompt for Face ID, Touch ID, Windows Hello, or a security key." />
                            <StepRow n={3} title="Save recovery codes" body="10 single-use codes to keep offline in case you lose all your devices." />
                        </ol>
                        <Button className="w-full bg-primary hover:bg-primary/90"
                            onClick={sendOtp} disabled={otpSending}
                            data-testid="sa-enrol-send-otp-btn">
                            {otpSending ? "Sending…" : "Start enrolment"}
                            <ArrowRight size={14} weight="bold" className="ml-1.5" />
                        </Button>
                    </div>
                )}

                {step === "otp" && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8" data-testid="sa-enrol-otp">
                        <div className="flex items-center gap-2 mb-1">
                            <Phone size={18} weight="duotone" className="text-primary" />
                            <h2 className="text-lg font-display font-semibold">Verify your phone</h2>
                        </div>
                        <p className="text-sm text-slate-400 mb-4">Enter the 6-digit code we just sent.</p>
                        {otpDevCode && (
                            <div className="mb-3 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="sa-enrol-dev-code">
                                DEV mode active — code is <span className="font-mono font-bold">{otpDevCode}</span>
                            </div>
                        )}
                        <Label className="text-xs font-semibold text-slate-300">OTP code</Label>
                        <Input
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            className="mt-1 bg-slate-950 border-slate-700 text-slate-100 tracking-widest text-lg text-center h-12"
                            data-testid="sa-enrol-otp-input"
                            autoFocus
                        />
                        <div className="flex gap-2 mt-4">
                            <Button
                                variant="outline"
                                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                onClick={sendOtp} disabled={otpSending}
                                data-testid="sa-enrol-resend-otp-btn">
                                Resend
                            </Button>
                            <Button
                                className="flex-1 bg-primary hover:bg-primary/90"
                                onClick={verifyOtp} disabled={verifying}
                                data-testid="sa-enrol-verify-otp-btn">
                                {verifying ? "Verifying…" : "Verify"}
                                <ArrowRight size={14} weight="bold" className="ml-1.5" />
                            </Button>
                        </div>
                    </div>
                )}

                {step === "passkey" && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8" data-testid="sa-enrol-passkey">
                        <div className="flex items-center gap-2 mb-1">
                            <Fingerprint size={18} weight="duotone" className="text-primary" />
                            <h2 className="text-lg font-display font-semibold">Register your passkey</h2>
                        </div>
                        <p className="text-sm text-slate-400 mb-4">
                            Your browser will prompt for biometrics (Face ID / Touch ID / Windows Hello) or a security key. You can register more devices later from Security settings.
                        </p>
                        <Label className="text-xs font-semibold text-slate-300">Device label (optional)</Label>
                        <Input
                            value={passkeyLabel}
                            onChange={(e) => setPasskeyLabel(e.target.value)}
                            placeholder={navigatorHint()}
                            className="mt-1 mb-4 bg-slate-950 border-slate-700 text-slate-100"
                            data-testid="sa-enrol-passkey-label"
                        />
                        <Button
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={registerPasskey} disabled={registering}
                            data-testid="sa-enrol-register-btn">
                            {registering ? "Waiting for your device…" : "Register passkey"}
                            <Fingerprint size={14} weight="bold" className="ml-1.5" />
                        </Button>
                    </div>
                )}

                {step === "done" && recoveryCodes.length > 0 && (
                    <RecoveryCodesPanel
                        codes={recoveryCodes}
                        onContinue={() => navigate("/super-admin")}
                    />
                )}

                {step === "done" && recoveryCodes.length === 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 text-center" data-testid="sa-enrol-error">
                        <Warning size={28} weight="duotone" className="text-amber-400 mx-auto mb-3" />
                        <div className="text-lg font-semibold text-slate-100 mb-1">Invite unavailable</div>
                        <div className="text-sm text-slate-400 mb-4">{error || "This invite has already been used."}</div>
                        <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800"
                            onClick={() => navigate("/")}
                            data-testid="sa-enrol-home-btn">
                            Back to Invisique™
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function StepRow({ n, title, body }) {
    return (
        <li className="flex gap-3">
            <div className="shrink-0 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 grid place-items-center text-[11px] font-bold text-slate-300">{n}</div>
            <div>
                <div className="font-semibold text-slate-100">{title}</div>
                <div className="text-slate-400 text-[13px]">{body}</div>
            </div>
        </li>
    );
}

function RecoveryCodesPanel({ codes, onContinue }) {
    const [ack, setAck] = useState(false);
    const copyAll = () => {
        navigator.clipboard.writeText(codes.join("\n"));
        toast.success("Copied to clipboard");
    };
    return (
        <div className="bg-slate-900/60 border border-emerald-500/30 rounded-2xl p-8" data-testid="sa-enrol-recovery">
            <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={20} weight="fill" className="text-emerald-400" />
                <h2 className="text-xl font-display font-semibold text-emerald-100">Passkey registered!</h2>
            </div>
            <p className="text-sm text-slate-400 mb-4">
                Save these <span className="font-semibold text-slate-100">recovery codes</span> somewhere secure (password manager, printed, offline). Each burns to log in once if you ever lose all your passkey devices.
            </p>
            <div className="rounded-lg bg-slate-950 border border-slate-800 p-4 font-mono text-sm grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3" data-testid="sa-enrol-codes-list">
                {codes.map((c) => (
                    <div key={c} className="text-slate-200 tracking-wider">{c}</div>
                ))}
            </div>
            <div className="flex gap-2 mb-4">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={copyAll} data-testid="sa-enrol-copy-codes">
                    <Copy size={12} weight="bold" className="mr-1.5" />
                    Copy all
                </Button>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer select-none mb-4">
                <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    className="mt-0.5 accent-emerald-500"
                    data-testid="sa-enrol-ack-checkbox"
                />
                <span>I have saved my recovery codes in a safe place.</span>
            </label>
            <Button
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={onContinue}
                disabled={!ack}
                data-testid="sa-enrol-continue-btn">
                Enter Super Admin
                <Sparkle size={14} weight="fill" className="ml-1.5" />
            </Button>
        </div>
    );
}

function navigatorHint() {
    const ua = navigator.userAgent;
    if (/Mac/i.test(ua)) return "Mac · Touch ID / Passkey";
    if (/Windows/i.test(ua)) return "Windows · Hello";
    if (/Android/i.test(ua)) return "Android · Screen lock";
    if (/iPhone|iPad/i.test(ua)) return "iPhone / iPad · Face ID";
    return "Device passkey";
}
